/* eslint-disable react-hooks/exhaustive-deps */

import { FC, PropsWithChildren, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { ControlledDialog } from '@zoopla-eng/zds-components/dialog';
import { Box } from '@zoopla-eng/zds-components/box';
import triggerTracking from '@blueprint/tracking/dist/Tracking';

import {
    AuthModalMode,
    AuthModalProps,
    AuthModalStepKeys,
    AuthModalStepsConfig
} from './AuthModal.types';
import { trackQuestionClose } from './AuthModal.tracking';
import { useUser, useSetIsLoggedIn } from '@zoopla/user';
import Preferences from './Preferences';
import { RegTabs } from './RegTabs/RegTabs';
import { User } from '../../graphql.generated';
import {
    isNewUser,
    shouldShowYourSituation,
    updateYourSituation,
    UpdateYourSituationType
} from './utils';
import {
    SituationQuickAccess,
    eventCategory as situationQuickAccessCategory
} from './section/SituationQuickAccess';
import { useSaveGuestSituationData } from './hooks/useSaveGuestSituationData';

const signInModalHeadingTn = 'Save properties';

const LazyYourSituationQuestion = dynamic(
    () => import('./YourSituationQuestion/YourSituationQuestion'),
    {
        ssr: false
    }
);

const LazyYourSituationQuestionResultButton = dynamic(
    () => import('./YourSituationQuestion/YourSituationResult'),
    {
        ssr: false
    }
);

const getInitialMode = (initialMode: AuthModalProps['initialMode']) =>
    initialMode === 'situation-quick-access'
        ? AuthModalStepKeys.SITUATION_QUICK_ACCESS
        : AuthModalStepKeys.FORM_TABS;

export const AuthModal: FC<PropsWithChildren<AuthModalProps>> = (props) => {
    const [isSocial, setIsSocial] = useState(false);
    const [currentStep, setCurrentStep] = useState<AuthModalStepKeys>(
        getInitialMode(props.initialMode)
    );
    const { saveSituationData, hasSavedAnswers, removeSavedAnswers } =
        useSaveGuestSituationData();
    const overrideInitialMode = useRef<AuthModalMode>();
    const { registered } = useUser();
    const setLoggedIn = useSetIsLoggedIn();

    const {
        heading,
        isShowing,
        close,
        actionAfterAuth,
        resetActionAfterAuth = () => null
    } = props;

    const socialAuthSignIn = async () => {
        setLoggedIn(true);
        await actionAfterAuth?.action();
        resetActionAfterAuth();
    };

    const socialAuthSignInAndClose = async () => {
        socialAuthSignIn();
        close();
    };

    const socialAuth = async (user?: User) => {
        setIsSocial(true);

        const userData = {
            traits: user?.traits,
            profile: user?.profile
        };
        sessionStorage?.setItem('auth-user-profile', JSON.stringify(userData));

        if (isNewUser({ registered }, user?.creationDate)) {
            localStorage.setItem('new_register_user', 'true');

            setCurrentStep(AuthModalStepKeys.PREFERENCES);
            return;
        }

        const situationStatus = updateYourSituation(user);

        if (situationStatus === UpdateYourSituationType.SELLER_FOLLOWUP) {
            setCurrentStep?.(AuthModalStepKeys.UPDATE_YOUR_SITUATION_SELLER);
            return;
        }

        if (situationStatus === UpdateYourSituationType.NEEDS_UPDATE) {
            setCurrentStep?.(AuthModalStepKeys.UPDATE_YOUR_SITUATION);
            return;
        }

        if (
            shouldShowYourSituation(user) ||
            situationStatus === UpdateYourSituationType.NO_SITUATION_ANSWER
        ) {
            setCurrentStep?.(AuthModalStepKeys.YOUR_SITUATION);
            return;
        }

        if (hasSavedAnswers()) {
            await saveSituationData();
            removeSavedAnswers();
        }

        socialAuthSignInAndClose();
    };

    // This runs when the user clicks the close button, outside the modal or uses the Escape key (not on submit)
    const toggleClosed = () => {
        const isSecondStep =
            currentStep === AuthModalStepKeys.PREFERENCES ||
            currentStep === AuthModalStepKeys.YOUR_SITUATION;
        // Email preference form is step 2 and Your situation question is step 3
        const stepNumber = isSocial ? 3 : 2;
        const secondStepEventLabel = `${actionAfterAuth?.eventLabel}_step_${stepNumber}`;

        triggerTracking({
            event: 'GAEvent',
            eventCategory: steps[currentStep].trackingCategory || '',
            eventAction: 'form closed',
            eventLabel: isSecondStep
                ? secondStepEventLabel
                : actionAfterAuth?.eventLabel
        });

        if (currentStep === AuthModalStepKeys.PREFERENCES) {
            // Still shows your situation when register if no sessionStorage with saved answers
            if (!hasSavedAnswers()) {
                setCurrentStep(AuthModalStepKeys.YOUR_SITUATION);
                return;
            }

            saveSituationData();
            removeSavedAnswers();
            resetActionAfterAuth();
            socialAuthSignInAndClose();
            return;
        }

        if (
            currentStep === AuthModalStepKeys.YOUR_SITUATION ||
            currentStep === AuthModalStepKeys.UPDATE_YOUR_SITUATION_SELLER ||
            currentStep === AuthModalStepKeys.UPDATE_YOUR_SITUATION
        ) {
            resetActionAfterAuth();
            socialAuthSignInAndClose();
            return;
        }

        // this is the case where we close before signin or register
        resetActionAfterAuth();
        close();
    };

    const changeToOtherMode = (newMode: AuthModalMode) => {
        overrideInitialMode.current = newMode;
        setCurrentStep(AuthModalStepKeys.FORM_TABS);
    };

    const onPreferenceFormSubmittedSuccessfully = () => {
        // Still shows your situation when register if no sessionStorage with saved answers
        if (!hasSavedAnswers()) {
            setCurrentStep(AuthModalStepKeys.YOUR_SITUATION);
            return;
        }

        saveSituationData();
        removeSavedAnswers();
        resetActionAfterAuth();
        socialAuthSignInAndClose();
    };

    const onSituationQuestionSkipClose = () => {
        toggleClosed();
    };

    const onSituationQuestionResultClose = (_ = '', lastQuestionId = '') => {
        trackQuestionClose(
            lastQuestionId,
            actionAfterAuth?.segment?.page || ''
        );
        toggleClosed();
    };

    const registration = (
        <RegTabs
            {...props}
            initialMode={overrideInitialMode.current || props.initialMode}
            onSocialAuth={socialAuth}
            setCurrentStep={setCurrentStep}
        />
    );

    const steps: AuthModalStepsConfig = {
        formTabs: {
            title: heading || signInModalHeadingTn,
            component: registration,
            trackingCategory:
                props.initialMode === 'sign-in' ? 'signin' : 'registration'
        },
        preferences: {
            component: (
                <Preferences
                    close={onPreferenceFormSubmittedSuccessfully}
                    actionAfterAuth={actionAfterAuth}
                />
            ),
            trackingCategory: 'registration'
        },
        situationQuickAccess: {
            title: heading || signInModalHeadingTn,
            component: (
                <SituationQuickAccess
                    selectMode={changeToOtherMode}
                    actionAfterAuth={actionAfterAuth}
                    close={close}
                    introText="Sign in or register to save these details and enjoy more from Zoopla."
                    onSocialAuth={socialAuth}
                    pageType={props.actionAfterAuth?.pageType}
                />
            ),
            trackingCategory: situationQuickAccessCategory
        },
        yourSituation: {
            title: 'Customise your experience',
            component: (
                <LazyYourSituationQuestion
                    page={actionAfterAuth?.segment?.page || 'unknown'}
                    onClose={onSituationQuestionSkipClose}
                    trackingIdentifier="auth: "
                    actionElement={
                        <LazyYourSituationQuestionResultButton
                            onClick={onSituationQuestionResultClose}
                        />
                    }
                />
            ),
            trackingCategory: 'registration'
        },
        updateYourSituation: {
            title: 'Update your situation',
            component: (
                <LazyYourSituationQuestion
                    questionType="update"
                    page={actionAfterAuth?.segment?.page || 'unknown'}
                    onClose={onSituationQuestionSkipClose}
                    trackingIdentifier="update: auth: "
                    actionElement={
                        <LazyYourSituationQuestionResultButton
                            onClick={onSituationQuestionResultClose}
                        />
                    }
                />
            ),
            trackingCategory: 'signin'
        },
        updateYourSituationSeller: {
            title: 'Update your situation',
            component: (
                <LazyYourSituationQuestion
                    questionType="seller"
                    page={actionAfterAuth?.segment?.page || 'unknown'}
                    onClose={onSituationQuestionSkipClose}
                    trackingIdentifier="update: auth: "
                    actionElement={
                        <LazyYourSituationQuestionResultButton
                            onClick={onSituationQuestionResultClose}
                        />
                    }
                />
            ),
            trackingCategory: 'signin'
        }
    };

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            toggleClosed();
        }
    };

    useEffect(() => {
        if (isShowing) {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: steps[currentStep].trackingCategory || '',
                eventAction: 'form shown',
                eventLabel: actionAfterAuth?.eventLabel
            });
        } else {
            setCurrentStep(getInitialMode(props.initialMode));
        }
    }, [isShowing, props.initialMode]);

    return (
        <ControlledDialog
            isOpen={isShowing}
            onOpenChange={onOpenChange}
            title={steps[currentStep].title}
            label={steps[currentStep].title || ''}
        >
            <Box pr="$S300" pl="$S100">
                {steps[currentStep].component}
            </Box>
        </ControlledDialog>
    );
};

export default AuthModal;
